/**
 * User roles, modelled on Plesk's Administrator / Reseller / Customer tiers
 * with an extra "staff" role for provider-side operators who should not be
 * able to change system-level configuration.
 *
 * - admin:    full control of the panel, settings, users and modules.
 * - staff:    manages clients, plans and subscriptions; no system settings.
 * - client:   tenant-facing role, scoped to their own client account (portal).
 * - reseller: operates a scoped panel view of the clients, plans and
 *             subscriptions they own (G20, ADR-0033). A reseller login is
 *             linked to exactly one reseller (`users.reseller_id`) and is
 *             denied the rest of the panel by default — it reaches only the
 *             routes explicitly opened and owner-scoped to it.
 */
export const USER_ROLES = ['admin', 'staff', 'client', 'reseller'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Roles that operate the provider-side panel *unrestricted*. Resellers also
 * operate a panel view, but a scoped one — deliberately excluded here so that
 * "unrestricted operator" and "owner-scoped operator" stay distinct: routes
 * default-closed to resellers and open per-route (ADR-0033).
 */
export const PANEL_ROLES: readonly UserRole[] = ['admin', 'staff'];

export function isPanelRole(role: UserRole): boolean {
  return PANEL_ROLES.includes(role);
}
